import {
  Cross2Icon,
  LightningBoltIcon,
  TrashIcon,
} from "@radix-ui/react-icons";
import { TaskStatus } from "client/openapi";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import IconTip from "components/Dashboard/PeoplePanel/IconTip";
import "components/Dashboard/PeoplePanel/index.css";
import {
  Dialog,
  DialogAction,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import { errorText } from "components/Forms/formsApi";
import { KindOptions } from "components/MessageTemplates/TriggerDialogs";
import { Select } from "components/Select";
import { Fragment, MouseEvent, useRef, useState } from "react";
import Notifications from "util/notifications";
import EditorDialog from "./AutomationsTabDialog";
import { taskTitle } from "./AutomationsTabModel";
import { CardTimeChip, WhenField } from "./AutomationsTabTiming";
import { useAutomationNames, useAutomationsApi, useReference } from "./hooks";
import {
  defaultTrigger,
  isTaskKind,
  kindOf,
  pickerKinds,
  kindTiming,
  TASK_STATUS_CHANGED,
  TASK_STATUSES,
} from "./kinds";
import {
  cardTaskStep,
  cardTime,
  clone,
  draftOf,
  entryCards,
  gateProblems,
  inAndBox,
  isLaneTask,
  Lane,
  newCard,
  sameTrigger,
  TASK_STEP,
  withEntry,
} from "./model";
import { AddButton, ChoiceSelect, FieldRow } from "./parts";
import type { ReferenceData } from "./reference";
import TriggerPicker, { TriggerKindGrid } from "./TriggerPicker";
import type {
  Automation,
  AutomationPatch,
  Gate,
  TaskStep,
  TimePosition,
  Trigger,
  TriggerCard,
  TriggerJoin,
} from "./types";
import {
  boxCardWords,
  cardTimeWords,
  kindSentence,
  laneTaskLine,
  plural,
  triggerSettings,
} from "./words";

/*
 * A lane's trigger cards: its entry (the first column, what starts each
 * run) and each gate later in it (what a run waits for). A stack is one
 * or more cards joined by AND or OR, as the CRM search joins its
 * conditions, with "+ Add a trigger" under it. In a stack of two or more,
 * and in a gate, each card holds its own timing; a one-card entry's is its
 * first column's.
 */

/** A booking's own choice, Only their first booking, as its card says it. */
const FIRST_BOOKING = "Only their first booking";

const JOINS: { value: TriggerJoin; label: string }[] = [
  { value: "and", label: "AND" },
  { value: "or", label: "OR" },
];

/** A stack's place in its lane: the entry (gate null), or a gate. */
interface Place {
  automation: Automation;
  gate: Gate | null;
}

function cardsOf({ automation, gate }: Place) {
  return gate ? gate.cards : entryCards(automation);
}

function logicOf({ automation, gate }: Place) {
  return gate ? gate.logic : automation.logic;
}

/** The task steps before a gate, the one right before it first. */
function tasksBefore(steps: Lane["steps"], gate: Gate | null): TaskStep[] {
  if (!gate) return [];
  return steps
    .filter(
      (s): s is TaskStep => s.action === "task" && (s.stage ?? 0) < gate.stage
    )
    .reverse();
}

/**
 * A new trigger of this kind for a stack. In a gate, a task kind waits for
 * a task an earlier step of the lane makes (the one right before it), as
 * a lane's waits did.
 */
function triggerFor(
  ref: ReferenceData,
  kind: string,
  tasks: TaskStep[] | null
): Trigger {
  const trigger = defaultTrigger(ref, kind);
  if (!tasks || !isTaskKind(kind)) return trigger;
  return {
    kind,
    options: {
      [TASK_STEP]: tasks[0]?.id ?? null,
      ...(kind === TASK_STATUS_CHANGED ? { to: TaskStatus.CANCELLED } : {}),
    },
    filters: [],
  };
}

/**
 * The lane with a stack changed: the entry through withEntry (its first
 * column's timing follows one card or several), or that gate.
 */
function patchFor(
  ref: ReferenceData,
  place: Place,
  cards: TriggerCard[],
  logic: TriggerJoin
): AutomationPatch {
  const { automation, gate } = place;
  if (gate)
    return {
      gates: automation.gates.map((g) =>
        g.id === gate.id ? { ...g, cards, logic } : g
      ),
    };
  const next = withEntry(ref, draftOf(automation), cards, logic);
  return {
    trigger: next.trigger,
    triggers: next.triggers ?? [],
    logic: next.logic ?? "and",
    steps: next.steps,
  };
}

/**
 * A gate card's fields: what it waits for (its kind) and the kind's own
 * choices; a task kind's task is one an earlier step of the lane makes,
 * and for a status change, the status. Its timing is its card's.
 */
function GateCardFields({
  trigger,
  tasks,
  onChange,
  boxed = false,
}: {
  trigger: Trigger;
  tasks: TaskStep[];
  onChange: (next: Trigger) => void;
  /** A card in an AND box of two or more, whose kinds it names as such a box does. */
  boxed?: boolean;
}) {
  const ref = useReference();
  const kind = kindOf(ref, trigger.kind);
  const step = cardTaskStep(trigger);
  return (
    <div>
      <FieldRow label="Trigger type" className="">
        <ChoiceSelect
          label="Trigger type"
          value={trigger.kind}
          options={pickerKinds(ref, boxed).map((k) => ({
            value: k.kind,
            label: k.label,
            hint: k.description,
          }))}
          onChange={(next) =>
            next !== trigger.kind && onChange(triggerFor(ref, next, tasks))
          }
          placeholder="Pick what it waits for"
          searchable
          className="w-full"
        />
      </FieldRow>
      {kind?.inCatalog && (
        <KindOptions
          kind={trigger.kind}
          options={trigger.options}
          set={(changes) =>
            onChange({
              ...trigger,
              options: { ...trigger.options, ...changes },
            })
          }
          catalog={ref.catalog}
          timing={false}
        />
      )}
      {isTaskKind(trigger.kind) && (
        <>
          <FieldRow label="Task">
            <ChoiceSelect<string | null>
              label="Task"
              value={step}
              options={tasks.map((t) => ({
                value: t.id,
                label: taskTitle(ref, t.task.name),
              }))}
              placeholder={
                tasks.length
                  ? "Pick the task"
                  : "Add a Create a task step before it first"
              }
              disabled={!tasks.length}
              onChange={(id) =>
                onChange({
                  ...trigger,
                  options: { ...trigger.options, [TASK_STEP]: id },
                })
              }
              className="w-full"
            />
          </FieldRow>
          {trigger.kind === TASK_STATUS_CHANGED && (
            <FieldRow label="Changes to">
              <ChoiceSelect
                label="Changes to"
                value={String(trigger.options?.to ?? TaskStatus.CANCELLED)}
                options={TASK_STATUSES.filter(
                  (s) => s.value !== TaskStatus.COMPLETE
                )}
                onChange={(to) =>
                  onChange({ ...trigger, options: { ...trigger.options, to } })
                }
              />
            </FieldRow>
          )}
        </>
      )}
    </div>
  );
}

/**
 * A stack's card dialog, the step dialog's size and style: an entry
 * card's Trigger type and choices, or a gate card's. A new card picks its
 * kind first and takes its own timing; a saved one's is its chip. While it
 * is on, a changed entry stops the runs still in the first column, and the
 * dialog says so when there are any. A gate's only card can delete the gate.
 * A new card on a box of one starts with AND or OR at the dialog's top: with
 * two, the lane's own select takes over.
 */
function CardDialog({
  place,
  index,
  returnFocus,
  onClose,
}: {
  place: Place;
  /** The card's place in its stack; null for a new card. */
  index: number | null;
  returnFocus: HTMLElement | null;
  onClose: () => void;
}) {
  const ref = useReference();
  const api = useAutomationsApi();
  const { automation, gate } = place;
  const cards = cardsOf(place);
  const saved = index === null ? null : cards[index];
  const tasks = tasksBefore(automation.steps, gate);
  const joining = index === null && cards.length === 1;
  const [joined, setJoined] = useState<TriggerJoin>("and");
  const logic = joining ? joined : logicOf(place);
  // Named as its box will name it: a new card makes one more.
  const boxed = inAndBox(
    logic,
    index === null ? cards.length + 1 : cards.length
  );
  const [trigger, setTrigger] = useState<Trigger | null>(
    saved ? clone(saved.trigger) : null
  );
  // A new card's own timing, set here; a saved one's is its chip.
  const [time, setTime] = useState<TimePosition | null>(null);
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const changed = !!trigger && (!saved || !sameTrigger(trigger, saved.trigger));
  const dirty = index === null ? !!trigger : changed;
  const drops =
    !gate && automation.on && changed ? automation.stats.firstPart : 0;
  const ownTime =
    index === null && !!trigger && !!kindTiming(ref, trigger.kind)
      ? time ?? cardTime(ref, trigger, null)
      : null;

  const pick = (kind: string) => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    setTrigger(triggerFor(ref, kind, gate ? tasks : null));
    setTime(null);
  };

  const change = (next: Trigger) => {
    setWarn(false);
    setProblem(null);
    // Another kind has other timing: it starts at the new kind's own.
    if (trigger && next.kind !== trigger.kind) setTime(null);
    setTrigger(next);
  };

  const join = (next: TriggerJoin) => {
    setWarn(false);
    setProblem(null);
    setJoined(next);
  };

  const save = () => {
    if (!trigger) return;
    setProblem(null);
    const card: TriggerCard =
      index === null
        ? { ...newCard(ref, trigger), time: ownTime }
        : {
            ...cards[index],
            trigger,
            time:
              trigger.kind === cards[index].trigger.kind
                ? cards[index].time
                : null,
          };
    const next =
      index === null
        ? [...cards, card]
        : cards.map((c, i) => (i === index ? card : c));
    if (gate) {
      const found = gateProblems(ref, automation, { ...gate, cards: next })[0];
      if (found) {
        setProblem(found);
        return;
      }
    }
    setBusy(true);
    api
      .updateAutomation(automation.id, patchFor(ref, place, next, logic))
      .then(onClose)
      .catch((e) => setProblem(errorText(e, "It could not be saved.")))
      .finally(() => setBusy(false));
  };

  /** Deletes the gate: its own steps stay, right after the column before it. */
  const removeGate = () => {
    if (!gate) return;
    setBusy(true);
    api
      .updateAutomation(automation.id, {
        gates: automation.gates.filter((g) => g.id !== gate.id),
      })
      .then(onClose)
      .catch((e) => {
        // Back to this dialog, which says why.
        setDeleting(false);
        setProblem(errorText(e, "It could not be deleted."));
      })
      .finally(() => setBusy(false));
  };

  const onlyCard = !!gate && index !== null && cards.length === 1;

  return (
    <EditorDialog
      title={index === null ? "Add a trigger" : "Edit the trigger"}
      fixed
      dirty={dirty}
      onClose={onClose}
      onWarn={() => setWarn(true)}
      returnFocus={returnFocus}
      bodyRef={bodyRef}
      footer={
        trigger && (
          <>
            {problem && (
              <p className="mb-3 text-sm text-red-600" role="alert">
                {problem}
              </p>
            )}
            {drops > 0 && (
              <p className="mb-3 text-sm text-amber-900" role="status">
                {`Saving drops ${plural(drops, "step")} that ${
                  drops === 1 ? "has" : "have"
                } not happened yet.`}
              </p>
            )}
            {warn && (
              <p
                className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
                role="status"
              >
                Save or cancel your changes first.
              </p>
            )}
            <div className="flex items-center gap-2">
              {onlyCard && (
                <IconTip label="Delete this gate">
                  <Button
                    color={ButtonColor.RED}
                    fill={ButtonFill.HOLLOW}
                    size={ButtonSize.SMALL}
                    aria-label="Delete this gate"
                    disabled={busy}
                    onClick={() => setDeleting(true)}
                  >
                    <TrashIcon />
                  </Button>
                </IconTip>
              )}
              <span className="flex-1" />
              <Button
                color={ButtonColor.SKYBLUE}
                fill={ButtonFill.HOLLOW}
                size={ButtonSize.SMALL}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                color={ButtonColor.GREEN}
                size={ButtonSize.SMALL}
                disabled={busy || !dirty}
                onClick={save}
              >
                Save
              </Button>
            </div>
          </>
        )
      }
    >
      {joining && (
        <div className="mb-3">
          <JoinSelect value={joined} onChange={join} />
        </div>
      )}
      {!trigger && <TriggerKindGrid boxed={boxed} onPick={pick} />}
      {trigger &&
        (gate ? (
          <GateCardFields
            trigger={trigger}
            tasks={tasks}
            boxed={boxed}
            onChange={change}
          />
        ) : (
          <TriggerPicker value={trigger} boxed={boxed} onChange={change} />
        ))}
      {trigger && ownTime && (
        <div className="mt-3">
          <WhenField
            words={cardTimeWords(ref, trigger, ownTime)}
            timing={kindTiming(ref, trigger.kind)}
            later={false}
            position={ownTime}
            onPick={(next) => {
              setWarn(false);
              setProblem(null);
              setTime(next);
            }}
          />
        </div>
      )}
      <Dialog open={deleting} onOpenChange={setDeleting}>
        {deleting && (
          <DialogContent className="dialog-content">
            <h2 className="mb-2 text-xl font-semibold">Delete this gate?</h2>
            <p className="text-sm text-gray-700">
              The steps after it stay, and no longer wait for it.
            </p>
            <DialogActions>
              <DialogAction
                primary={true}
                color={ButtonColor.RED}
                disabled={busy}
                onClick={removeGate}
              >
                Delete
              </DialogAction>
              <DialogClose asChild>
                <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
              </DialogClose>
            </DialogActions>
          </DialogContent>
        )}
      </Dialog>
    </EditorDialog>
  );
}

/**
 * One card: the kind in words as its title, its own timing (in a stack of
 * two or more, and in a gate) as a chip, and each of its own settings
 * (which form, which column, the task, the filters); the entry's first
 * card also says the automation's trouble. A click
 * opens its dialog; while two or more remain, it can be removed.
 */
function StackCard({
  place,
  card,
  first,
  ownTime,
  boxed,
  open,
  onOpen,
  onRemove,
  onRetime,
}: {
  place: Place;
  card: TriggerCard;
  first: boolean;
  ownTime: boolean;
  /** In an AND box of two or more: a CRM column card reads "In Trial". */
  boxed: boolean;
  open: boolean;
  onOpen: (anchor: HTMLElement) => void;
  onRemove: (() => void) | null;
  onRetime: (time: TimePosition) => void;
}) {
  const ref = useReference();
  const names = useAutomationNames();
  const { automation, gate } = place;
  const { trigger } = card;
  const box = boxed ? boxCardWords(ref, trigger) : null;
  const kind = box?.title ?? kindSentence(ref, trigger.kind);
  const said = isLaneTask(trigger)
    ? [laneTaskLine(ref, automation.steps, trigger)]
    : box
    ? [box.line]
    : triggerSettings(ref, trigger, names);
  const settings =
    trigger.kind === "meeting_booked" &&
    trigger.options?.first_only &&
    !said.includes(FIRST_BOOKING)
      ? [...said, FIRST_BOOKING]
      : said;
  const lines = settings.length
    ? settings
    : isTaskKind(trigger.kind)
    ? ["Any task"]
    : [];
  const time = ownTime ? card.time ?? cardTime(ref, trigger, null) : null;
  const cardOf = (e: MouseEvent<HTMLElement>) =>
    (e.currentTarget.closest("article") as HTMLElement) ?? e.currentTarget;

  return (
    <article
      aria-label={`Trigger: ${kind}`}
      aria-current={open || undefined}
      onClick={(e) => onOpen(e.currentTarget)}
      className={`group cursor-pointer rounded-lg border p-3 transition-shadow ${
        open
          ? "border-green-500 ring-2 ring-green-500/20"
          : "border-gray-200 hover:border-gray-300"
      } ${automation.on ? "bg-white" : "bg-neutral-50"}`}
    >
      <div className="flex items-center gap-2">
        <span className="flex min-w-0 flex-1 items-center gap-1.5 text-xs font-semibold text-gray-600">
          <span
            className="inline-flex flex-none [&>svg]:h-3.5 [&>svg]:w-3.5"
            aria-hidden
          >
            <LightningBoltIcon />
          </span>
          Trigger
        </span>
        {onRemove && (
          // Quiet until the card is pointed at or has focus.
          <IconTip label="Remove trigger">
            <button
              type="button"
              className="people-panel__icon -my-1.5 -mr-1.5 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
              aria-label="Remove trigger"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              <Cross2Icon />
            </button>
          </IconTip>
        )}
      </div>
      <button
        type="button"
        data-card-title
        className={`mt-1.5 block w-full break-words text-left text-sm font-semibold leading-5 ${
          automation.on ? "text-gray-900" : "text-gray-500"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          onOpen(cardOf(e));
        }}
      >
        {kind}
      </button>
      {time && (
        <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
          <CardTimeChip trigger={trigger} time={time} onPick={onRetime} />
        </div>
      )}
      {lines.map((line, i) => (
        <p
          // Two filters can read the same ("Name contains “Check in”").
          key={`${i}:${line}`}
          className="mt-0.5 break-words text-[13px] leading-[18px] text-gray-600"
        >
          {line}
        </p>
      ))}
      {!gate && first && automation.lastError && (
        <p className="mt-1.5 break-words text-xs text-red-600">
          {automation.lastError}
        </p>
      )}
    </article>
  );
}

/** A stack's one AND or OR, the CRM search's select. */
function JoinSelect({
  value,
  onLane = false,
  onChange,
}: {
  value: TriggerJoin;
  /** A lane scrolls sideways and would cut its list off, so the list opens out of it. */
  onLane?: boolean;
  onChange: (next: TriggerJoin) => void;
}) {
  return (
    <div className="w-24">
      <Select
        aria-label="AND or OR"
        isSearchable={false}
        isClearable={false}
        menuPlacement="auto"
        menuPortalTarget={onLane ? document.body : undefined}
        menuPosition={onLane ? "fixed" : undefined}
        value={JOINS.find((j) => j.value === value)}
        options={JOINS}
        onChange={(o: { value: TriggerJoin } | null) =>
          o && o.value !== value && onChange(o.value)
        }
      />
    </div>
  );
}

/**
 * Between a stack's cards: its select in the first gap; the later gaps say
 * it.
 */
function Join({
  value,
  control,
  onChange,
}: {
  value: TriggerJoin;
  control: boolean;
  onChange: (next: TriggerJoin) => void;
}) {
  const label = value === "or" ? "OR" : "AND";
  if (!control)
    return (
      <p className="py-1.5 text-center text-xs font-semibold text-gray-500">
        {label}
      </p>
    );
  return (
    <div className="automation-join flex justify-center py-1.5">
      <JoinSelect value={value} onLane onChange={onChange} />
    </div>
  );
}

/**
 * A stack of trigger cards, the entry's (gate null) or a gate's: its cards
 * joined by its AND or OR, and "+ Add a trigger" under them. A change made
 * on the lane (a card removed, the AND or OR, a card's own timing) saves at
 * once; one that drops what the first column has scheduled asks first.
 */
export default function TriggerStack({
  automation,
  gate,
}: {
  automation: Automation;
  gate: Gate | null;
}) {
  const ref = useReference();
  const api = useAutomationsApi();
  const place: Place = { automation, gate };
  const cards = cardsOf(place);
  const logic = logicOf(place);
  const several = cards.length > 1;
  // The card whose dialog is open ("new": Add a trigger's).
  const [open, setOpen] = useState<{
    index: number | null;
    anchor: HTMLElement | null;
  } | null>(null);
  // A change on the lane that drops scheduled steps, waiting for its confirm.
  const [confirm, setConfirm] = useState<AutomationPatch | null>(null);
  const drops = !gate && automation.on ? automation.stats.firstPart : 0;

  const save = (patch: AutomationPatch) =>
    api
      .updateAutomation(automation.id, patch)
      .catch((e) =>
        Notifications.error(errorText(e, "That could not be changed."))
      );
  const change = (next: TriggerCard[], nextLogic: TriggerJoin) => {
    const patch = patchFor(ref, place, next, nextLogic);
    if (drops > 0) setConfirm(patch);
    else save(patch);
  };

  return (
    <>
      <div>
        {cards.map((card, i) => (
          <Fragment key={card.id ?? `${i}:${card.trigger.kind}`}>
            {i > 0 && (
              <Join
                value={logic}
                control={i === 1}
                onChange={(next) => change(cards, next)}
              />
            )}
            <StackCard
              place={place}
              card={card}
              first={i === 0}
              ownTime={several || !!gate}
              boxed={inAndBox(logic, cards.length)}
              open={open?.index === i}
              onOpen={(anchor) => setOpen({ index: i, anchor })}
              onRemove={
                several
                  ? () =>
                      change(
                        cards.filter((_, j) => j !== i),
                        logic
                      )
                  : null
              }
              onRetime={(time) =>
                change(
                  cards.map((c, j) => (j === i ? { ...c, time } : c)),
                  logic
                )
              }
            />
          </Fragment>
        ))}
      </div>
      <div className="mt-2 flex">
        <AddButton
          label="Add a trigger"
          onAdd={(anchor) => setOpen({ index: null, anchor })}
        />
      </div>
      {open && (
        <CardDialog
          place={place}
          index={open.index}
          returnFocus={
            open.anchor?.matches("button")
              ? open.anchor
              : open.anchor?.querySelector<HTMLElement>("[data-card-title]") ??
                null
          }
          onClose={() => setOpen(null)}
        />
      )}
      <Dialog
        open={confirm !== null}
        onOpenChange={(next) => !next && setConfirm(null)}
      >
        {confirm && (
          <DialogContent className="dialog-content">
            <h2 className="mb-2 text-xl font-semibold">
              Change the trigger of {automation.name}?
            </h2>
            <p className="text-sm text-gray-700">
              {`${plural(drops, "step")} ${
                drops === 1 ? "has" : "have"
              } not happened yet. Changing the trigger drops ${
                drops === 1 ? "it" : "them"
              }.`}
            </p>
            <DialogActions>
              <DialogAction
                primary={true}
                color={ButtonColor.RED}
                onClick={() => {
                  save(confirm);
                  setConfirm(null);
                }}
              >
                Change
              </DialogAction>
              <DialogClose asChild>
                <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
              </DialogClose>
            </DialogActions>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}

/** A new gate's card fields, for the step dialog's Add a trigger gate: its kind, then its choices. */
export { GateCardFields, tasksBefore, triggerFor };
